import { computed, Directive, effect, ElementRef, inject, input } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';

import { ButtonGroupDirective } from './button-group.directive';

const buttonVariants = cva('tt-button', {
  variants: {
    variant: {
      primary: 'tt-button--primary',
      secondary: 'tt-button--secondary',
      accent: 'tt-button--accent',
      destructive: 'tt-button--destructive',
      /* Status */
      ready: 'tt-button--ready',
      inReview: 'tt-button--in-review',
      inProgress: 'tt-button--in-progress',
      approved: 'tt-button--approved',
      published: 'tt-button--published',
      disabled: 'tt-button--disabled',
      transparent: 'tt-button--transparent',
      outline: 'tt-button--outline',
    },
    emphasis: {
      bold: 'tt-button--bold',
      regular: 'tt-button--regular',
      subtle: 'tt-button--subtle',
      minimal: 'tt-button--minimal',
    },
    size: {
      medium: 'tt-button--medium',
      small: 'tt-button--small',
      icon: 'tt-button--icon',
      xSmall: 'tt-button--x-small',
    },
    font: {
      default: 'tt-button--font-default',
      small: 'tt-button--font-small',
    },
    type: {
      useParentWidth: 'tt-button--use-parent-width',
    },
  },
  defaultVariants: {
    variant: 'primary',
    emphasis: 'bold',
    size: 'medium',
    font: 'default',
  },
});

type ButtonVariants = VariantProps<typeof buttonVariants>;

@Directive({
  selector: 'tt-button, button[ttButton], a[ttButton]',
  standalone: true,
  host: {
    '[class]': 'computedClass()',
    '[attr.disabled]': 'isDisabled() ? true : null',
  },
})
export class ButtonDirective {
  private group = inject(ButtonGroupDirective, { optional: true, host: true });

  private el = inject(ElementRef<HTMLElement>);
  private spinnerEl?: HTMLElement;

  public isBusy = input(false);
  public disabled = input(false);

  public font = input<ButtonVariants['font']>();
  public variant = input<ButtonVariants['variant']>();
  public emphasis = input<ButtonVariants['emphasis']>();
  public size = input<ButtonVariants['size']>();
  public type = input<ButtonVariants['type']>();

  protected isDisabled = computed(() => {
    const own = this.disabled();
    const groupDisabled = this.group?.disabled() ?? false;

    return own || groupDisabled || this.isBusy();
  });

  protected computedClass = computed(() => {
    const variants = buttonVariants({
      variant: this.variant(),
      emphasis: this.emphasis(),
      size: this.size(),
      type: this.type(),
      font: this.font(),
    });

    const isBusy = this.isBusy() ? 'tt-button--busy' : '';
    const isDisabled = this.isDisabled() ? 'tt-button--disabled' : '';

    return ['tt-button', variants, isBusy, isDisabled].join(' ');
  });

  constructor() {
    effect(() => {
      const busy = this.isBusy();

      if (busy && !this.spinnerEl) {
        const spinner = document.createElement('span');
        spinner.className = 'tt-button_spinner';

        this.el.nativeElement.appendChild(spinner);

        this.spinnerEl = spinner;
      }

      if (!busy && this.spinnerEl) {
        this.spinnerEl.remove();
        this.spinnerEl = undefined;
      }
    });
  }
}

@Directive({
  selector: 'tt-button[ttActionButton], [ttButton][ttActionButton]',
  standalone: true,
  host: {
    class: 'tt-action-button',
  },
})
export class ActionButtonDirective {}
